/**
 * UPT-2124 J4a (spec `web/SPEC-UPT-2124-J4.md` §2 n° 2, §3) — enregistre les HUIT outils `ui_*` dans le registre des outils vocaux (J2a) au montage
 * du chrome des portails, avec l'outil générique `confirm` (J2d : « oui » / « non » d'un geste confirmé) ; les retire au démontage. Un seul point de
 * montage (`UiVoiceHost`), actif quand la voix est visible : le modèle vocal reçoit ces outils par le `session.update` de l'ouverture du canal.
 * Les valeurs vivantes (traduction, navigation, chemin, menu) passent par des références : les outils ne sont enregistrés qu'une fois.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useIntl } from '@/react-intl';
import { useRuntimeNavigation } from '@/uptk/core/hooks/use_runtime_navigation';
import { registerConfirmTool } from '../voiceConfirm';
import { postVoiceIntent } from '../voiceApi';
import { registerVoiceTool } from '../voiceToolRegistry';
import { flattenNavigationEntries } from './uiNavEntries';
import { createUiTools, stopUiGestures } from './uiTools';

export function useUiVoiceTools(): void {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const runtimeNavigation = useRuntimeNavigation();
  const live = useRef({ intl, navigate, pathname: location.pathname, nodes: runtimeNavigation.nodes });
  live.current = { intl, navigate, pathname: location.pathname, nodes: runtimeNavigation.nodes };

  useEffect(() => {
    const translate = (id: string, values?: Record<string, string | number>): string => live.current.intl.formatMessage({ id }, values);
    const tools = createUiTools({
      document,
      t: translate,
      navigate: (to) => {
        if (typeof to === 'number') {
          void live.current.navigate(to);
        } else {
          void live.current.navigate(to);
        }
      },
      navEntries: () => flattenNavigationEntries(live.current.nodes, (id) => translate(id)),
      pathname: () => live.current.pathname,
      journal: (body) => {
        void postVoiceIntent(body);
      },
    });
    const releases = tools.map((tool) => registerVoiceTool(tool));
    const releaseConfirm = registerConfirmTool();
    return () => {
      releases.forEach((release) => release());
      releaseConfirm();
      stopUiGestures();
    };
  }, []);
}

export default useUiVoiceTools;
